import React, { useEffect, useMemo, useState } from 'react';
import { jss, ThemeProvider } from 'react-jss';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { BrowserRouter } from 'react-router-dom';
import { ComponentSettings, ThemeFluent } from '@bingads-webui-theme-2018/theme-fluent';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { isTestEnvironment } from '@bingads-webui-campaign/global-app-init';
import { DefaultScopeProvider } from '@bingads-webui-react/scope-service';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LegacyI18nContext } from '@bingads-webui-react/legacy-i18n-context';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { buildLegacyI18n, validationLocalization } from '@bingads-webui/build-legacy-i18n';
import ablEnforcementLegacyStrings from '@bingads-webui-cc-react/abl-enforcement-legacy-strings';
import ablLegacyStrings from '@bingads-webui-cc-react/address-tax-form-legacy-strings';
import pidlLegacyString from '@bingads-webui-cc-react/pidl-payment-method-actions-base-legacy-strings';
import entityUtilLegacyString from '@bingads-webui-clientcenter/entity-utils-legacy-strings';
import termAndConditionsLegacyString from '@bingads-webui-cc-react/terms-and-conditions-legacy-strings';
import autoPbcLightboxLegacyStrings from '@bingads-webui-cc-react/auto-pbc-lightbox-legacy-strings';
import offlinePiLegacyStrings from '@bingads-webui-cc-react/check-bank-transfer-legacy-strings';
import addFundsLegacyString from '@bingads-webui-cc-react/signup-add-funds-legacy-strings';
import { aggregateStrings } from '@bingads-webui-cc-react/account-business-location-legacy-strings';
import { fileDraggerLegacyString } from '@bingads-webui-campaign-react/file-dragger';
import { GlobalEventCallbackContextProvider } from '@bingads-webui-campaign-react/global-event-callback-context';
import { msSupportChatLocalization, requestCallModalLocalization } from '@bingads-webui-react/bingads-backbone-header-strings';
import { PaymentContextProvider } from '@bingads-webui-cc-react/payment-context';
import { WrappedFloatingBannerContainer } from '@bingads-webui-react/notification-center';
import { SidePanelContextProvider } from '@bingads-webui-campaign-react/side-panel';
import { PromotionBannerContextProvider } from '@bingads-webui-campaign-react/promotion-banner';
import { createGenerateId } from '@bingads-webui-campaign-react/custom-jss-config';
import { legacyServerContextProps, ServerContextWithActionProvider } from '@bingads-webui-campaign-react/server-context';
import { getInstrumentation, getInstrumentationService, InstrumentationWithServiceProvider } from '@bingads-webui-campaign-react/pmax-lite-instrumentation';
import { OverreactDataFetcher } from '@bingads-webui-campaign-react/pmax-overreact-data-fetcher';
import { initLegacyOdata } from '@bingads-webui-campaign-react/pmax-lite-wizard-utils';
import { SignupConversionPixelsReporter } from '@bingads-webui-campaign-react/pmax-lite-wizard-hooks';
import { WebMetrics } from '@bingads-webui-react/web-metrics';
import { legacyI18n as UiNextLegacyI18n, initializeUiNextLegacyI18n } from '@bingads-webui-campaign/legacy-i18n/loader/ui-next/index';

import { CustomerJourneyProviderWithContext } from './customer-journey-provider-with-context';
import { SignupFlowProviderWithContext } from './signup-flow-provider-with-context';
import { PreferenceContextProvider } from './preference-context-provider';
import { imageExperienceLegacyStrings } from './i18n/image-experience-legacy-strings';
import { adExtensionsLegacyStrings } from './i18n/ad-extensions-legacy-strings';
import { AppCore } from './features';
import mainI18n from './main.l10n';

require.context('@bingads-webui-react/rich-text-editor/src/icons');
require.context('@bingads-webui-campaign-react/pmax-lite-ms-ads-sdk/media');

const isTestEnv = isTestEnvironment(document.domain);

const originPath = window.location.pathname;

// Use a custom jss insertion point so that it will have higher priority than global css
jss.setup({
  insertionPoint: document.getElementById('jss-insertion-point'),
  createGenerateId,
});

function getIndexVersion() {
  const regex = /^[\d|\w]{8}$/;
  const matches = regex.exec(document.lastChild.nodeValue);

  return matches ? matches[0] : '';
}

const AppRoot = (props) => {
  const [i18nObjects, setI18n] = useState({ i18n: {}, legacyI18n: {} });
  const { serverContext, paymentContext, devConfig } = props;
  const {
    CurrentCustomer,
    CurrentAccount,
    CurrentUser,
    Token,
  } = serverContext;

  initLegacyOdata(serverContext);

  const { Id: cid } = CurrentCustomer || {};
  const { Id: aid } = CurrentAccount || {};
  const { Id: uid } = CurrentUser || {};

  const defaultScope = { cid, aid, uid };

  const subSystemName = 'pmax-lite-signup';

  const instrumentation = useMemo(() => getInstrumentation({
    serverContext,
    devConfig,
    subSystemName,
  }), [devConfig, serverContext]);

  const instrumentationService = useMemo(() => getInstrumentationService({
    instrumentation,
    serverContext,
  }), [instrumentation, serverContext]);

  useEffect(() => {
    mainI18n.ready().then(async (i18n) => {
      if (isTestEnv) {
        // eslint-disable-next-line no-console
        console.log('Main i18n is ready', _.isEmpty(i18n) ? 'but return i18n object is empty' : '');
      }

      // eslint-disable-next-line no-param-reassign
      i18n.failFast = !isTestEnv ? 'SAFE' : i18n.failFast;
      const legacyI18n = buildLegacyI18n(i18n, [
        ValidationStringMap,
        validationLocalization,
        ablEnforcementLegacyStrings,
        autoPbcLightboxLegacyStrings,
        ablLegacyStrings,
        offlinePiLegacyStrings,
        pidlLegacyString,
        entityUtilLegacyString,
        termAndConditionsLegacyString,
        addFundsLegacyString,
        msSupportChatLocalization,
        requestCallModalLocalization,
        aggregateStrings,
        fileDraggerLegacyString,
        imageExperienceLegacyStrings,
        adExtensionsLegacyStrings,
      ]);
      setI18n({
        i18n,
        legacyI18n,
      });

      if (isTestEnv) {
        // eslint-disable-next-line no-console
        console.log('i18nObjects is seted');
      }

      // legacy i18n initialization required for legacy components to work properly
      UiNextLegacyI18n.setMain(i18n);

      await UiNextLegacyI18n.loadAllStrings();

      initializeUiNextLegacyI18n();
    });
  }, []);

  if (CurrentUser) {
    const indexVersion = getIndexVersion();
    const usageReportScenario = instrumentation.usageReport.create();
    const signUpBundleResumeActivity = usageReportScenario.signUpBundleResume.create();
    signUpBundleResumeActivity.trace(`Resume sign up bundle. indexVersion=${indexVersion}`);
    if (!cid || !uid || !aid || !Token) {
      signUpBundleResumeActivity.error(`Invalid server context. cid: ${cid}; uid: ${uid}; aid: ${aid}; isTokenEmpty: ${_.isEmpty(Token)}`);
    }
  }

  if (_.isEmpty(i18nObjects.i18n)) {
    if (isTestEnv) {
      // eslint-disable-next-line no-console
      console.log('i18n is not ready yet, rendering null');
    }
    return null;
  }

  return (
    <BrowserRouter basename={originPath}>
      <DefaultScopeProvider
        scope={defaultScope}
        allowedKeys={[
          'cid',
          'aid',
          'uid',
          'campaignId',
          'assetGroupId',
          'adGroupId',
          'cco', // coupon code
          'ccl', // coupon class
          'cacheId',
          'email',
          'signature',
          'cn_reseller',
          'flights',
        ]}
      >
        <ThemeProvider theme={{ components: ComponentSettings, core: ThemeFluent }}>
          <FluentProvider theme={webLightTheme}>
            <I18nContext.Provider value={i18nObjects.i18n}>
              <LegacyI18nContext.Provider value={i18nObjects.legacyI18n}>
                <InstrumentationWithServiceProvider context={instrumentationService}>
                  <PaymentContextProvider paymentContext={paymentContext}>
                    <ServerContextWithActionProvider context={serverContext}>
                      <PreferenceContextProvider>
                        <OverreactDataFetcher>
                          <SignupFlowProviderWithContext>
                            <SidePanelContextProvider>
                              <GlobalEventCallbackContextProvider>
                                <PromotionBannerContextProvider>
                                  <CustomerJourneyProviderWithContext>
                                    <AppCore i18nObjects={i18nObjects} devConfig={devConfig} />
                                    <WrappedFloatingBannerContainer />
                                    <WebMetrics />
                                    <SignupConversionPixelsReporter />
                                  </CustomerJourneyProviderWithContext>
                                </PromotionBannerContextProvider>
                              </GlobalEventCallbackContextProvider>
                            </SidePanelContextProvider>
                          </SignupFlowProviderWithContext>
                        </OverreactDataFetcher>
                      </PreferenceContextProvider>
                    </ServerContextWithActionProvider>
                  </PaymentContextProvider>
                </InstrumentationWithServiceProvider>
              </LegacyI18nContext.Provider>
            </I18nContext.Provider>
          </FluentProvider>
        </ThemeProvider>
      </DefaultScopeProvider>
    </BrowserRouter>
  );
};

AppRoot.propTypes = {
  serverContext: legacyServerContextProps.isRequired,
  paymentContext: PropTypes.shape({}).isRequired,
  devConfig: PropTypes.shape({
    isInDevelopmentMode: PropTypes.bool.isRequired,
  }).isRequired,
};

export default AppRoot;
